import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { lazy, Suspense, type ComponentType } from 'react';
import { isIsoDate, type PermKey } from '@farmos/shared';
import { useAuth } from '@/auth/AuthProvider';
import { LoginPage } from '@/auth/LoginPage';
import { AppShell } from '@/components/layout/AppShell';
import { canSeeToday, NAV } from '@/components/layout/nav';
import { Spinner } from '@/components/ui/feedback';
import { DASH_TABS, type DashTab } from '@/features/dashboard/tabs';
import { NotFound } from './NotFound';

export interface RouterContext {
  auth: ReturnType<typeof useAuth>;
}

function page(loader: () => Promise<{ default: ComponentType }>) {
  const C = lazy(loader);
  return function LazyPage() {
    return (
      <Suspense fallback={<Spinner />}>
        <C />
      </Suspense>
    );
  };
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: ({ context }) => {
    if (context.auth.session) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context }) => {
    if (!context.auth.session) throw redirect({ to: '/login' });
  },
  component: AppShell,
});

function guard(perm: PermKey) {
  return ({ context }: { context: RouterContext }) => {
    if (!context.auth.perms[perm]) throw redirect({ to: '/' });
  };
}

function Home() {
  // Managers open on the dashboard's Today tab; everyone else lands on their own screen
  // (harvest workers go straight to entry), and on the dashboard only if that is all they have.
  const { perms } = useAuth();
  if (canSeeToday(perms)) return <Navigate to="/dashboard" replace />;
  const first =
    NAV.find((n) => n.perm !== 'dash' && perms[n.perm]) ?? NAV.find((n) => perms[n.perm]);
  return first ? <Navigate to={first.to} replace /> : <NoAccess />;
}

const NoAccess = page(() => import('./NoAccess'));

const indexRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: Home });

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  // alerts open a tab
  validateSearch: (s: Record<string, unknown>): { tab?: DashTab } =>
    DASH_TABS.includes(s.tab as DashTab) ? { tab: s.tab as DashTab } : {},
  beforeLoad: guard('dash'),
  component: page(() => import('@/features/dashboard/DashboardPage')),
});
const harvestRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/harvest',
  beforeLoad: guard('harvest'),
  component: page(() => import('@/features/harvest/HarvestPage')),
});
const salesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sales',
  // alerts link to the day that needs a look
  validateSearch: (s: Record<string, unknown>): { date?: string } =>
    isIsoDate(s.date) ? { date: s.date } : {},
  beforeLoad: guard('sales'),
  component: page(() => import('@/features/sales/SalesPage')),
});
const purchasesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/purchases',
  beforeLoad: guard('purch'),
  component: page(() => import('@/features/purchases/PurchasesPage')),
});
const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tasks',
  // search and links open a task's drawer
  validateSearch: (s: Record<string, unknown>): { task?: string } =>
    typeof s.task === 'string' && /^[0-9a-f-]{36}$/.test(s.task) ? { task: s.task } : {},
  beforeLoad: guard('tasks'),
  component: page(() => import('@/features/tasks/TasksPage')),
});
const shiftsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/shifts',
  beforeLoad: guard('shifts'),
  component: page(() => import('@/features/shifts/ShiftsPage')),
});
const mgmtRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/mgmt',
  // a task links to the report or meeting it came from; "new" opens an empty one
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'reports' | 'meetings'; report?: string; meeting?: string } => {
    const id = (v: unknown) =>
      typeof v === 'string' && /^(new|[0-9a-f-]{36})$/.test(v) ? v : undefined;
    const tab = s.tab === 'reports' || s.tab === 'meetings' ? s.tab : undefined;
    return {
      ...(tab && { tab }),
      ...(id(s.report) && { report: id(s.report) }),
      ...(id(s.meeting) && { meeting: id(s.meeting) }),
    };
  },
  beforeLoad: guard('mgmt'),
  component: page(() => import('@/features/mgmt/MgmtPage')),
});
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  beforeLoad: guard('settings'),
  component: page(() => import('@/features/settings/SettingsLayout')),
});
const settingsIndex = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  component: () => <Navigate to="/settings/$section" params={{ section: 'clients' }} replace />,
});
const settingsSection = createRoute({
  getParentRoute: () => settingsRoute,
  path: '$section',
  component: page(() => import('@/features/settings/SettingsSection')),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    indexRoute,
    dashboardRoute,
    harvestRoute,
    salesRoute,
    purchasesRoute,
    tasksRoute,
    shiftsRoute,
    mgmtRoute,
    settingsRoute.addChildren([settingsIndex, settingsSection]),
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { auth: undefined! },
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
